import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, FolderOpen } from 'lucide-react';
import '../../../styles/admin/ERPDepartments.css';


const ERPDepartments = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    couleur: '#01500A',
    ordre: 0
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/api/erp/departments?actif_only=false', {
      });
      setDepartments(response.data.departments);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDepartment(null);
    setFormData({
      nom: '',
      description: '',
      couleur: '#01500A',
      ordre: departments.length + 1
    });
    setShowModal(true);
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      nom: department.nom,
      description: department.description || '',
      couleur: department.couleur || '#01500A',
      ordre: department.ordre || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingDepartment) {
        await api.put(
          `/api/erp/departments/${editingDepartment.id}`,
          formData,
        );
      } else {
        await api.post(
          '/api/erp/departments',
          formData,
        );
      }

      setShowModal(false);
      fetchDepartments();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du département');
    }
  };

  const handleDelete = async (departmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver ce département?')) {
      return;
    }

    try {
      await api.delete(`/api/erp/departments/${departmentId}`);
      fetchDepartments();
    } catch (error) {
      alert('Erreur lors de la suppression du département');
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-departments-page">
      <div className="erp-departments-header">
        <div className="erp-departments-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
          <div className="header-title-section">
            <h1><FolderOpen size={32} /> Départements</h1>
            <p>Organiser les services par département</p>
          </div>
        </div>
      </div>

      <div className="erp-departments-content">
        <div className="departments-toolbar">
          <div className="departments-count">
            {departments.filter(d => d.actif).length} département(s) actif(s)
          </div>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={20} />
            Nouveau département
          </button>
        </div>

        <div className="departments-grid">
          {departments.length === 0 ? (
            <div className="empty-state">
              <FolderOpen size={48} color="#ccc" />
              <p>Aucun département créé</p>
              <button className="btn-primary" onClick={handleCreate}>
                <Plus size={20} />
                Créer le premier département
              </button>
            </div>
          ) : (
            departments.map(dept => (
              <div 
                key={dept.id} 
                className={`department-card ${!dept.actif ? 'inactive' : ''}`}
                style={{ borderLeftColor: dept.couleur }}
              >
                <div className="department-card-header">
                  <div className="department-header-left">
                    <div 
                      className="department-color-badge" 
                      style={{ background: dept.couleur }}
                    />
                    <h3>{dept.nom}</h3>
                  </div>
                  <div className="department-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(dept)}
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => handleDelete(dept.id)}
                      title="Désactiver"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div className="department-card-body">
                  {dept.description && (
                    <p className="department-description">{dept.description}</p>
                  )}
                  <div className="department-meta">
                    Ordre d'affichage: {dept.ordre}
                  </div>
                  {!dept.actif && (
                    <div className="department-inactive-badge">Inactif</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDepartment ? 'Modifier le département' : 'Nouveau département'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="department-form">
              <div className="form-group">
                <label>Nom du département *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  placeholder="Ex: FERTILISATION"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Description du département..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Couleur</label>
                  <div className="color-picker-group">
                    <input
                      type="color"
                      value={formData.couleur}
                      onChange={(e) => setFormData({...formData, couleur: e.target.value})}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={formData.couleur}
                      onChange={(e) => setFormData({...formData, couleur: e.target.value})}
                      placeholder="#01500A"
                      className="color-text-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Ordre d'affichage</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.ordre}
                    onChange={(e) => setFormData({...formData, ordre: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingDepartment ? 'Mettre à jour' : 'Créer le département'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPDepartments;
